// ตัวช่วยที่หน้าจำหน่ายนักเรียนใช้ร่วมกับการ์ด "ใส่ข้อมูลย้อนหลัง"
//
// แยกออกมาเพราะทั้งสองส่วนต้องพูดเรื่องเดียวกันด้วยคำเดียวกัน — ชั้นล่าสุด ประเภทการออก
// และรายการสาเหตุ ถ้าต่างคนต่างนิยาม รายการสาเหตุจะเพี้ยนกันจนรายงานนับรวมไม่ได้
import React from "react";
import { Badge } from "react-bootstrap";

// สาเหตุการจำหน่ายที่ใช้จริงในทะเบียน — เลือกจากรายการเพื่อให้รายงานนับรวมกันได้
// ("อื่นๆ" เปิดช่องพิมพ์เอง เพราะระเบียบต้นสังกัดมีกรณีที่ไม่ได้อยู่ในรายการนี้)
export const WITHDRAW_REASONS = [
  "ย้ายสถานศึกษา",
  "ลาออก",
  "ขาดเรียนนาน",
  "ผู้ปกครองย้ายภูมิลำเนา",
  "ปัญหาด้านสุขภาพ",
  "ปัญหาด้านค่าใช้จ่าย",
  "ให้ออก",
  "ถึงแก่กรรม",
  "อื่นๆ",
];

export const TYPE_LABEL = { graduate: "สำเร็จการศึกษา", withdraw: "จำหน่าย" };

export const todayISO = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

// ค่าวันที่จาก API เป็น DATE ที่ mysql2 คืนเป็น Date object พอผ่าน JSON กลายเป็น UTC
// ของเมื่อวาน 17:00Z — ตัดสิบตัวแรกของสตริงจะได้วันก่อนหน้าหนึ่งวัน ต้องอ่านเป็นเวลาท้องถิ่น
export const toInputDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// สาเหตุที่เก็บไว้เป็นข้อความ -> ช่องเลือกในฟอร์ม ค่าที่ไม่อยู่ในรายการคือของเดิมที่พิมพ์เอง
// (หรือรายการสาเหตุถูกแก้ไปแล้ว) ต้องตกลงช่อง "อื่นๆ" ไม่ใช่หายไปเงียบๆ ตอนกดแก้
export const reasonToForm = (reason) => {
  const text = String(reason || "").trim();
  if (!text) return { reasonChoice: WITHDRAW_REASONS[0], reasonOther: "" };
  return WITHDRAW_REASONS.includes(text)
    ? { reasonChoice: text, reasonOther: "" }
    : { reasonChoice: "อื่นๆ", reasonOther: text };
};

// "ชั้นมัธยมศึกษาปีที่ 3" + ห้อง 12 + ปี 2569 -> "ม.3/12 (2569)"
export const classLabel = (row) => {
  if (!row?.c_class) return "-";
  const level = String(row.c_class).replace(/\D/g, "") || row.c_class;
  const room = row.gen_room_no ? `/${row.gen_room_no}` : "";
  return `ม.${level}${room}${row.gen_year ? ` (${row.gen_year})` : ""}`;
};

// ชั้นศาสนา: "7/ا (2569)" — ic_class เป็นข้อความปน "2".."10" กับ "AIP1".."STP3"
// จึงพิมพ์ตรงๆ ไม่ต้องดึงตัวเลขออกมาเหมือนชั้นสามัญ
export const islamicClassLabel = (row) => {
  if (!row?.ic_class) return "-";
  const room = row.isl_room_no ? `/${row.isl_room_no}` : "";
  return `${row.ic_class}${room}${row.isl_year ? ` (${row.isl_year})` : ""}`;
};

// ใช้ในกล่องยืนยันและหัวฟอร์ม — บอกทั้งสองภาคในบรรทัดเดียว
export const bothClassesLabel = (row) =>
  `สามัญ ${classLabel(row || {})} · ศาสนา ${islamicClassLabel(row || {})}`;

// ม.3 และ ม.6 คือชั้นสุดท้ายของหลักสูตร คนที่ออกตอนอยู่ชั้นนี้ส่วนใหญ่คือคนที่เรียนจบ
// จึงตั้งค่าเริ่มต้นให้ แต่ยังเปลี่ยนเป็น "จำหน่าย" ได้ถ้าเป็นคนที่ออกก่อนจบจริงๆ
export const suggestedType = (row) => {
  const level = Number(String(row?.c_class || "").replace(/\D/g, ""));
  return level === 3 || level === 6 ? "graduate" : "withdraw";
};

export const statusBadge = (row) => {
  if (String(row.st_status) === "0") {
    const type = row.ex_type || "withdraw";
    return <Badge bg={type === "graduate" ? "info" : "secondary"}>{TYPE_LABEL[type]}</Badge>;
  }
  if (String(row.st_status) === "3") return <Badge bg="warning">นักเรียนใหม่</Badge>;
  return <Badge bg="success">กำลังศึกษาอยู่</Badge>;
};

// ปีการศึกษาไทยจบสิ้นเดือนมีนาคมของปีถัดไป — "2568" -> 2026-03-31
// ใช้เป็นค่าเริ่มต้นของวันที่ตอนใส่ย้อนหลัง ซึ่งเป็นวันที่ใกล้ความจริงที่สุดเท่าที่เดาได้
// สำหรับรุ่นที่จบไปแล้ว (ยังแก้เป็นวันจริงได้ถ้ามีในเอกสารของทะเบียน)
export const academicYearEndISO = (buddhistYear) => {
  const year = Number(buddhistYear);
  if (!Number.isInteger(year) || year < 2400 || year > 2700) return "";
  return `${year - 543 + 1}-03-31`;
};
